import { FilterParamsModel } from "core/sharedmodels/filter/FilterParamsModel";
import { PaginationModel } from "core/sharedmodels/filter/PaginationModel";
import { ForloebOverviewConstants } from "../forloebOverviewConstants";
import { useEffect, useState } from "react";
import { DropdownOption } from "core/components/dropdown/dropdown";
import { ForloebOverviewTypeEnum } from "core/sharedmodels/forloeb/forloebOverviewTypeEnum";
import { BrugerIndstillingTypeKeyEnum } from "core/sharedmodels/brugerIndstilling/brugerIndstillingTypeKeyEnum";
import useUserSettings, { useInitializeViaUserSettings } from "core/hooks/useUserSettings";
import { ForloebOverviewFiltersState, forloebOverviewFiltersReducer, setSearch } from "../forloebOverviewFiltersSlice";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { StorageKeys } from "infrastructure/storage/storageKeys";
import { DeepPartial } from "@reduxjs/toolkit";
import { useHistory, useLocation } from "react-router-dom";

export type ForloebOverviewLocationState = { viewType?: ForloebOverviewTypeEnum };


const initialFilterParamsState = (initialSearch?: string): FilterParamsModel => ({
  paginationModel: new PaginationModel(1, ForloebOverviewConstants.defaultPageSize, 0),
  search: initialSearch ?? "",
  advancedSearch: false,
  showUnoccupiedStillinger: false,
  showAnonymized: false,
  showForloebKladder: false,
  showInactive: false
});

const pageSizeDropdownOptions = ForloebOverviewConstants.pageSizeSelectOptions.map(opt => ({ label: `${opt}`, value: opt } as DropdownOption<number>));

/**
 * Incapsulates states/effects regarding generic view options for forloebsOverview (View-switches, pagination, etc.)
 *
 * Also handles loading in saved options from current/previous session (user-settings)
 */
const useForloebOverviewViewOptions = () => {
  const { userSettingsIsLoading, getUserSettingValue, setUserSettingValue } = useUserSettings();
  const location = useLocation<ForloebOverviewLocationState>();
  const history = useHistory();

  const forloebOverviewFilterSliceState = useAppSelector(forloebOverviewFiltersReducer) as ForloebOverviewFiltersState;
  const dispatch = useAppDispatch();

  // Determine initial view type from navigation state (if provided)
  const navigationViewType = location?.state?.viewType;

  const [filterParams, setFilterParams] = useState<FilterParamsModel>(initialFilterParamsState(forloebOverviewFilterSliceState.search));
  const [forloebOverviewType, setForloebOverviewType] = useState<ForloebOverviewTypeEnum>(
    navigationViewType ?? ForloebOverviewTypeEnum.ForloebOverblikStillingerView
  );
  const [overlappendeVisningState, setOverlappendeVisningState] = useState<boolean>(true);
  const [visHorizontaleLinjerState, setVisHorizontaleLinjerState] = useState<boolean>(true);

  const [isCurrentPageLoadedFromStorage, setIsCurrentPageLoadedFromStorage] = useState<boolean>(false);

  useEffect(() => {
    if (location?.state?.viewType !== undefined) {
      setForloebOverviewType(location.state.viewType);
      history.replace(location.pathname, {});
    }
  }, [location?.state?.viewType, history, location.pathname]);

  useInitializeViaUserSettings(userSettingsIsLoading, () => {
      const savedPageSize = getUserSettingValue(BrugerIndstillingTypeKeyEnum.PageForloebsoverblikPageSize, ForloebOverviewConstants.defaultPageSize);
      const savedViewOption = getUserSettingValue(BrugerIndstillingTypeKeyEnum.PageForloebsoverblikViewOption, ForloebOverviewTypeEnum.ForloebOverblikStillingerView);
      const advancedSearch = getUserSettingValue(BrugerIndstillingTypeKeyEnum.PageForloebsoverblikUseAdvancedSearch, false);

      const showUnoccupiedStillinger = getUserSettingValue(BrugerIndstillingTypeKeyEnum.PageForloebsoverblikUseUbesatteStillinger, false);
      const showForloebKladder = getUserSettingValue<boolean>(BrugerIndstillingTypeKeyEnum.PageForloebsoverblikUseForloebKladder, false);
      const showAnonymized = getUserSettingValue<boolean>(BrugerIndstillingTypeKeyEnum.PageForloebsoverblikShowAnonymized, false);
      const showInactive = getUserSettingValue<boolean>(BrugerIndstillingTypeKeyEnum.PageForloebsoverblikShowInactive, false);
      const showHorizontalLines = getUserSettingValue<boolean>(BrugerIndstillingTypeKeyEnum.PageForloebsoverblikUseHorizontalLines, true);
      const showOverlap = getUserSettingValue<boolean>(BrugerIndstillingTypeKeyEnum.PageForloebsoverblikUseUdvidOverlap, true);

      const pageNumberFromStorage = localStorage.getItem(StorageKeys.forloebsoverblikPageNumber);
      const pageNumberIsInStorage = !!pageNumberFromStorage;
      const pageNumber = pageNumberIsInStorage ? Number(pageNumberFromStorage) : ForloebOverviewConstants.defaultPageNumber;
      setIsCurrentPageLoadedFromStorage(pageNumberIsInStorage);

      // Only use saved view option if not navigating with a specific view type
      if (!navigationViewType) {
        setForloebOverviewType(savedViewOption);
      }
      setVisHorizontaleLinjerState(showHorizontalLines);
      setOverlappendeVisningState(showOverlap);

      setFilterParams(prev => (
        {
          ...prev,
          showUnoccupiedStillinger: showUnoccupiedStillinger,
          showForloebKladder: showForloebKladder,
          showAnonymized: showAnonymized,
          showInactive: showInactive,
          advancedSearch: advancedSearch,
          paginationModel: {
            page: pageNumber,
            pageLength: savedPageSize,
            recordCount: 0
          }
        }
      ));
  });

  /**
   * Handles updating filterParams and setting the pageNumber.
   *
   * @param valuesToUpdate - Will update filterParams with only the properties defined.
   * @param pageNumber - The page-number we want to go to (will also update localStorage).
  */
  const updateFilterParamsAndGoToPage = (valuesToUpdate: DeepPartial<FilterParamsModel>, pageNumber: number) => {
    setFilterParams(prev => {
      return {
        ...prev,
        ...valuesToUpdate,
        paginationModel: { ...prev.paginationModel, ...valuesToUpdate.paginationModel, page: pageNumber }}
    });

    localStorage.setItem(StorageKeys.forloebsoverblikPageNumber, pageNumber.toString()); //Only stored in current session
    setIsCurrentPageLoadedFromStorage(false); //User interaction - no longer loaded via saved options
  }

  const onUseAdvancedSearchChanged = (useAdvancedSearch: boolean) => {
    updateFilterParamsAndGoToPage({ advancedSearch: useAdvancedSearch }, 1);
    setUserSettingValue(BrugerIndstillingTypeKeyEnum.PageForloebsoverblikUseAdvancedSearch, useAdvancedSearch);
  }

  const onUseOverlappendeVisningChanged = (checked: boolean) => {
    setOverlappendeVisningState(checked);
    setUserSettingValue(BrugerIndstillingTypeKeyEnum.PageForloebsoverblikUseUdvidOverlap, checked);
  }

  const onShowAnonymizedVisningChanged = (checked: boolean) => {
    updateFilterParamsAndGoToPage({ showAnonymized: checked }, 1);
    setUserSettingValue(BrugerIndstillingTypeKeyEnum.PageForloebsoverblikShowAnonymized, checked);
  }

  const onShowInactiveStillingerChanged = (checked: boolean) => {
    updateFilterParamsAndGoToPage({ showInactive: checked }, 1);
    setUserSettingValue(BrugerIndstillingTypeKeyEnum.PageForloebsoverblikShowInactive, checked);
  }

  const onShowHorizontaleLinjerChanged = (checked: boolean) => {
    setVisHorizontaleLinjerState(checked);
    setUserSettingValue(BrugerIndstillingTypeKeyEnum.PageForloebsoverblikUseHorizontalLines, checked);
  }

  const onShowUnoccupiedStillingerChanged = (checked: boolean) => {
    updateFilterParamsAndGoToPage({ showUnoccupiedStillinger: checked }, 1);
    setUserSettingValue(BrugerIndstillingTypeKeyEnum.PageForloebsoverblikUseUbesatteStillinger, checked);
  }

  const onShowForloebKladderChanged = (checked: boolean) => {
    updateFilterParamsAndGoToPage({ showForloebKladder: checked }, 1);
    setUserSettingValue(BrugerIndstillingTypeKeyEnum.PageForloebsoverblikUseForloebKladder, checked);
  }

  const onPageSizeChange = (pageLength?: number) => {
    if (!pageLength) return;
    updateFilterParamsAndGoToPage({ paginationModel: { pageLength: pageLength } }, 1);
    setUserSettingValue(BrugerIndstillingTypeKeyEnum.PageForloebsoverblikPageSize, pageLength);
  }

  const onSearchChanged = (searchQuery: string) => {
    updateFilterParamsAndGoToPage({ search: searchQuery }, 1);
    dispatch(setSearch(searchQuery));
  }

  const onPageChanged = async (page: number) => {
    updateFilterParamsAndGoToPage({}, page);
  };

  const onViewOptionChange = (viewOptionType: ForloebOverviewTypeEnum) => {
    setForloebOverviewType(viewOptionType);
    updateFilterParamsAndGoToPage({}, 1);
    setUserSettingValue(BrugerIndstillingTypeKeyEnum.PageForloebsoverblikViewOption, viewOptionType);
  }

  return {
    filterParams,
    forloebOverviewType,
    overlappendeVisningState,
    visHorizontaleLinjerState,
    pageSizeDropdownOptions,
    isCurrentPageLoadedFromStorage,
    setIsCurrentPageLoadedFromStorage,
    onUseAdvancedSearchChanged,
    onUseOverlappendeVisningChanged,
    onShowAnonymizedVisningChanged,
    onShowInactiveStillingerChanged,
    onShowHorizontaleLinjerChanged,
    onShowUnoccupiedStillingerChanged,
    onShowForloebKladderChanged,
    onPageSizeChange,
    onSearchChanged,
    onPageChanged,
    onViewOptionChange
  }
}

export default useForloebOverviewViewOptions;
